import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Loader2, Settings, Clock, TrendingUp, FileText, Send, Settings2 } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TimesheetDeadlineSettings } from '@/components/TimesheetDeadlineSettings';
import { TimeEntryRulesAccordion } from '@/components/settings/TimeEntryRulesAccordion';
import { UtilisationSettings } from '@/components/UtilisationSettings';
import { WorkingHoursConfigurationCompact } from '@/components/settings/WorkingHoursConfigurationCompact';
import { ConflictResolutionDialog } from '@/components/settings/ConflictResolutionDialog';
import { ValidationErrorSummary } from '@/components/settings/ValidationErrorSummary';
import { HealthCheckDialog } from '@/components/settings/HealthCheckDialog';
import { HealthCheckBadge } from '@/components/settings/HealthCheckBadge';
import { useSettingsValidation } from '@/hooks/useSettingsValidation';
import type { ValidationError } from '@/hooks/useSettingsValidation';
import { runHealthCheck } from '@/services/settingsHealthCheck';
import type { HealthCheckResult, HealthCheckFinding } from '@/types/healthCheck';
import type { 
  TimesheetDeadlineSettings as DeadlineSettingsType,
  UtilisationSettings as UtilisationSettingsType,
  OrganisationTimesheetSettings,
  WorkingProfile,
  PolicyViolationSettings,
  PolicyLimits,
  ApprovalScreenSettings,
  OvertimeSettings,
} from '@/types/timesheetSettings';
import { OvertimeSettingsTab } from '@/components/settings/OvertimeSettingsTab';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import { toast } from 'sonner';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { usePermissions } from '@/contexts/PermissionContext';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';
import { workingPatternToProfile } from '@/services/settingsRepository';
import { useSettings } from '@/contexts/SettingsContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useOvertimeTypes } from '@/hooks/useOvertimeTypes';
import { formatDistanceToNow } from 'date-fns';

interface SettingsPageProps {
  onNavigate?: (path: string) => void;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ onNavigate }) => {
  const { mode, isDemo, isLive } = useScreenDataMode('settings-general');
  const { weekStartDay, setWeekStartDay: setGlobalWeekStartDay, refreshSettings } = useSettings();

  // Permission gates — each controls one tab's editability
  const {
    canManageSystemSettings,
    canManagePolicySettings,
    canManageDeadlines,
    canManageUtilisationSettings,
    canManageTimeEntrySettings,
    canManageWorkTypeSettings,
  } = usePermissions();

  // canEdit[tab] = true means the user can save changes on that tab
  // canManageSystemSettings() is the master switch — if false, all tabs are read-only
  const canEditWorkingHours     = canManageSystemSettings() && canManagePolicySettings();
  const canEditDeadlines        = canManageSystemSettings() && canManageDeadlines();
  const canEditTimeEntryRules   = canManageSystemSettings() && canManageTimeEntrySettings();
  const canEditWorkType         = canManageSystemSettings() && canManageWorkTypeSettings();
  const canEditUtilisation      = canManageSystemSettings() && canManageUtilisationSettings();
  
  const [deadlineSettings, setDeadlineSettings] = useState<DeadlineSettingsType | null>(null);
  const [utilisationSettings, setUtilisationSettings] = useState<UtilisationSettingsType | null>(null);
  const [workingProfile, setWorkingProfile] = useState<WorkingProfile | null>(null);
  const [policyEnforcement, setPolicyEnforcement] = useState<PolicyViolationSettings | null>(null);
  const [policyLimits, setPolicyLimits] = useState<PolicyLimits | undefined>(undefined);
  const [approvalScreen, setApprovalScreen] = useState<ApprovalScreenSettings>(DEFAULT_TIMESHEET_SETTINGS.approvalScreen);
  const [fullSettings, setFullSettings] = useState<OrganisationTimesheetSettings | null>(null);
  const [rowVersion, setRowVersion] = useState<string | undefined>(undefined);
  
  // Data requirements state - weekStartDay now comes from Settings context
  const [showRole, setShowRole] = useState(false);
  const [requireRole, setRequireRole] = useState(false);
  const [showCostCentre, setShowCostCentre] = useState(true);
  const [requireCostCentre, setRequireCostCentre] = useState(true);
  const [requireDescription, setRequireDescription] = useState<'never' | 'always'>('always');
  
  // Project selection state
  const [projectSelectionScope, setProjectSelectionScope] = useState<'all' | 'assigned'>('all');
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [showProjectTimeRemaining, setShowProjectTimeRemaining] = useState(true);
  
  // Time entry validation state
  const [minimumEntryDuration, setMinimumEntryDuration] = useState<'6min' | '15min' | '30min' | '1hour'>('15min');
  const [timeRounding, setTimeRounding] = useState<'none' | '15min' | '30min' | '1hour'>('none');
  const [allowNegativeHours, setAllowNegativeHours] = useState(false);
  
  // Date range restrictions state
  const [historicalEntryLimit, setHistoricalEntryLimit] = useState(4);
  const [allowFutureDatedEntries, setAllowFutureDatedEntries] = useState<'no' | '1week' | '2weeks' | '4weeks' | 'unlimited'>('2weeks');
  
  // Smart Features state
  const [enableCopyPreviousWeek, setEnableCopyPreviousWeek] = useState(true);
  const [copyDescriptions, setCopyDescriptions] = useState(true);
  const [copyOnlyBillable, setCopyOnlyBillable] = useState(false);
  const [enableAutoSmartFill, setEnableAutoSmartFill] = useState(true);
  const [enableAiInsights, setEnableAiInsights] = useState(true);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('working-hours');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  // Track which tabs have unsaved changes
  const [tabsWithChanges, setTabsWithChanges] = useState<Set<string>>(new Set());
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const { validateAllSettings } = useSettingsValidation();
  
  // Health Check state
  const [healthCheckResult, setHealthCheckResult] = useState<HealthCheckResult | null>(null);
  const [showHealthCheckDialog, setShowHealthCheckDialog] = useState(false);
  
  // Conflict resolution state
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictServerSettings, setConflictServerSettings] = useState<OrganisationTimesheetSettings | null>(null);
  
  // Last saved state
  const [lastSaved, setLastSaved] = useState<{ timestamp: Date; user: string } | null>(null);

  // Overtime settings state
  const [overtimeSettings, setOvertimeSettings] = useState<OvertimeSettings>(DEFAULT_TIMESHEET_SETTINGS.overtime);

  // Company context and overtime types from API
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.id;
  const { overtimeTypes, refetch: refetchOvertimeTypes } = useOvertimeTypes({ includeInactive: true, screenId: 'settings-general' });
  
  // Helper to mark a tab as having changes
  const markTabAsChanged = useCallback((tabId: string) => {
    setTabsWithChanges(prev => new Set(prev).add(tabId));
    setHasUnsavedChanges(true);
  }, []);

  const handleOvertimeSettingsChange = useCallback((settings: OvertimeSettings) => {
    setOvertimeSettings(settings);
    markTabAsChanged('overtime');
  }, [markTabAsChanged]);

  const handleDeadlineChange = useCallback((settings: DeadlineSettingsType, isValid: boolean) => {
    setDeadlineSettings(settings);
    markTabAsChanged('submissions-deadlines');
  }, [markTabAsChanged]);

  const handleUtilisationChange = useCallback((settings: UtilisationSettingsType, isValid: boolean) => {
    setUtilisationSettings(settings);
    markTabAsChanged('utilisation');
  }, [markTabAsChanged]);

  const handleWorkingProfileChange = useCallback((profile: WorkingProfile) => {
    // Working hours are read-only from Resource Manager
    // This callback is kept for compatibility but should not trigger changes
    // setWorkingProfile(profile);
    // setHasUnsavedChanges(true);
  }, []);

  const handlePolicyEnforcementChange = useCallback((enforcement: PolicyViolationSettings) => {
    setPolicyEnforcement(enforcement);
    markTabAsChanged('working-hours');
  }, [markTabAsChanged]);

  const handlePolicyLimitsChange = useCallback((limits: PolicyLimits | undefined) => {
    setPolicyLimits(limits);
    markTabAsChanged('working-hours');
  }, [markTabAsChanged]);

  const handleApprovalScreenChange = useCallback((settings: ApprovalScreenSettings) => {
    setApprovalScreen(settings);
    markTabAsChanged('working-hours');
  }, [markTabAsChanged]);

  // Data requirements change handlers with unsaved tracking
  const handleWeekStartDayChange = useCallback((day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => {
    setGlobalWeekStartDay(day);
    markTabAsChanged('general');
  }, [setGlobalWeekStartDay, markTabAsChanged]);

  const handleShowRoleChange = useCallback((show: boolean) => {
    setShowRole(show);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleRequireRoleChange = useCallback((require: boolean) => {
    setRequireRole(require);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleShowCostCentreChange = useCallback((show: boolean) => {
    setShowCostCentre(show);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleRequireCostCentreChange = useCallback((require: boolean) => {
    setRequireCostCentre(require);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleRequireDescriptionChange = useCallback((value: 'never' | 'always') => {
    setRequireDescription(value);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  // Project selection change handlers
  const handleProjectSelectionScopeChange = useCallback((scope: 'all' | 'assigned') => {
    setProjectSelectionScope(scope);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleShowArchivedProjectsChange = useCallback((show: boolean) => {
    setShowArchivedProjects(show);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleShowProjectTimeRemainingChange = useCallback((show: boolean) => {
    setShowProjectTimeRemaining(show);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  // Time entry validation change handlers
  const handleMinimumEntryDurationChange = useCallback((duration: '6min' | '15min' | '30min' | '1hour') => {
    setMinimumEntryDuration(duration);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleTimeRoundingChange = useCallback((rounding: 'none' | '15min' | '30min' | '1hour') => {
    setTimeRounding(rounding);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleAllowNegativeHoursChange = useCallback((allow: boolean) => {
    setAllowNegativeHours(allow);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleHistoricalEntryLimitChange = useCallback((weeks: number) => {
    setHistoricalEntryLimit(weeks);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleAllowFutureDatedEntriesChange = useCallback((allow: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited') => {
    setAllowFutureDatedEntries(allow);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  // Smart Features change handlers
  const handleEnableCopyPreviousWeekChange = useCallback((enable: boolean) => {
    setEnableCopyPreviousWeek(enable);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleCopyDescriptionsChange= useCallback((copy: boolean) => {
    setCopyDescriptions(copy);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleCopyOnlyBillableChange = useCallback((copy: boolean) => {
    setCopyOnlyBillable(copy);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleEnableAutoSmartFillChange = useCallback((enable: boolean) => {
    setEnableAutoSmartFill(enable);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleEnableAiInsightsChange = useCallback((enable: boolean) => {
    setEnableAiInsights(enable);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch both timesheet settings and working pattern in parallel
      const [settings, workingPattern] = await Promise.all([
        settingsRepository.getOrganisationTimesheetSettings(mode),
        settingsRepository.getOrganisationWorkingPattern(mode),
      ]);
      
      setFullSettings(settings);
      setDeadlineSettings(settings.deadline);
      setUtilisationSettings(settings.utilisation);
      
      // Load data requirements settings
      // weekStartDay is managed by Settings context, so we don't set it here
      setShowRole(settings.showRole);
      setRequireRole(settings.requireRole);
      setShowCostCentre(settings.showCostCentre);
      setRequireCostCentre(settings.requireCostCentre);
      
      // Load smart features settings from deadline settings
      setEnableCopyPreviousWeek(settings.deadline.enableCopyPreviousWeek);
      setCopyDescriptions(settings.deadline.copyDescriptions);
      setCopyOnlyBillable(settings.deadline.copyOnlyBillable);
      setEnableAutoSmartFill(settings.deadline.enableAutoSmartFill ?? true);
      setEnableAiInsights(settings.deadline.enableAiInsights ?? true);
      
      // Load new data requirements fields from settings
      setRequireDescription(settings.requireDescription ?? 'always');
      setMinimumEntryDuration(settings.minimumEntryDuration ?? '6min');
      setTimeRounding(settings.timeRounding ?? 'none');
      setAllowNegativeHours(settings.allowNegativeHours ?? false);
      setHistoricalEntryLimit(settings.historicalEntryLimit ?? 4);
      setAllowFutureDatedEntries(settings.allowFutureDatedEntries ?? '2weeks');
      
      // Reset unsaved changes flag and tab tracking after loading
      setHasUnsavedChanges(false);
      setTabsWithChanges(new Set());
      
      // Convert WorkingPattern from API to WorkingProfile format for the UI
      // This ensures we read from the same source (OrganizationWorkingPattern) that we write to
      const loadedWorkingProfile = workingPatternToProfile(workingPattern);
      setWorkingProfile(loadedWorkingProfile);
      
      setPolicyEnforcement(settings.policyViolations || {
        monitoringFrequency: 'week',
        suppressDailyWhenWeeklyExceeded: true,
        consolidateDailyWarnings: true,
        checks: {
          'daily-overage': { enabled: true, threshold: 1.25, severity: 'warning' },
          'weekly-overage': { enabled: true, threshold: 1.10, severity: 'warning' },
          'legal-daily-maximum': { enabled: false, severity: 'critical' },
          'legal-weekly-maximum': { enabled: false, severity: 'critical' },
          'missing-hours': { enabled: true, threshold: 0.80, severity: 'info' },
          'non-working-day': { enabled: true, severity: 'info' },
          'daily-missing-hours': { enabled: true, threshold: 0.80, severity: 'info' },
          'weekly-missing-hours': { enabled: true, threshold: 0.80, severity: 'info' },
          'daily-non-working-day': { enabled: true, severity: 'info' },
          'weekly-non-working-day': { enabled: true, severity: 'info' }
        },
        displayMode: 'smart',
        showInfoAlerts: true,
        preset: 'balanced'
      });
      setPolicyLimits(settings.policyLimits);
      setApprovalScreen(settings.approvalScreen ?? DEFAULT_TIMESHEET_SETTINGS.approvalScreen);
      setOvertimeSettings(settings.overtime ?? DEFAULT_TIMESHEET_SETTINGS.overtime);
      
      if ('rowVersion' in settings) {
        setRowVersion(settings.rowVersion);
      }
      
      // Run health check after loading settings
      const healthResult = runHealthCheck(settings);
      setHealthCheckResult(healthResult);
    } catch (err) {
      console.error('Error loading settings:', err);
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to load settings. Please try again.';
      setError(errorMessage);
      
      if (isLive) {
        toast.error('Failed to load settings', {
          description: 'Could not connect to API. Please ensure the backend is running or switch to demo mode.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [mode]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const ensureLiveSettings = async (): Promise<{ settings: OrganisationTimesheetSettings; version: string } | null> => {
    if (fullSettings && rowVersion !== undefined) {
      return { settings: fullSettings, version: rowVersion };
    }
    try {
      const freshSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
      setFullSettings(freshSettings);
      const freshVersion = ('rowVersion' in freshSettings) ? (freshSettings.rowVersion ?? '') : undefined;
      if (freshVersion !== undefined) {
        setRowVersion(freshVersion);
        return { settings: freshSettings, version: freshVersion };
      }
    } catch {
      // fall through
    }
    toast.error('Cannot save', {
      description: 'Could not retrieve settings version from the API. Please reload the page.',
    });
    return null;
  };

  const handleSaveUtilisation = async (settings: UtilisationSettingsType) => {
    setIsSaving(true);
    
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }
      const updatedSettings = await settingsRepository.updateUtilisationSettings(
        settings,
        currentSettings,
        currentRowVersion,
        mode
      );
      
      setUtilisationSettings(settings);
      setFullSettings(updatedSettings);
      
      if ('rowVersion' in updatedSettings) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      setHasUnsavedChanges(false);
      setTabsWithChanges(new Set());
      
      // Set last saved timestamp
      setLastSaved({
        timestamp: new Date(),
        user: 'You'
      });
      
      // Re-run health check after save
      const healthResult = runHealthCheck(updatedSettings);
      setHealthCheckResult(healthResult);
      
      toast.success('Settings saved successfully', {
        description: 'Your changes have been applied',
      });
    } catch (err) {
      console.error('Error saving utilisation settings:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        try {
          const serverSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
          setConflictServerSettings(serverSettings);
          setShowConflictDialog(true);
        } catch {
          toast.error('Settings conflict', {
            description: 'Settings were modified by another user. Reloading...',
          });
          await loadData();
        }
      } else {
        toast.error('Failed to save settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveWorkingHoursSettings = async () => {
    if (!workingProfile || !policyEnforcement) return;
    
    setIsSaving(true);
    
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }
      
      // Only save policy enforcement settings, not working hours
      // Working hours are managed in Resource Manager and should remain read-only
      const updatedSettings = await settingsRepository.updateWorkingHoursSettings(
        workingProfile, // Pass current working profile but don't allow changes
        policyEnforcement,
        policyLimits,
        approvalScreen,
        currentSettings,
        currentRowVersion,
        mode
      );
      
      // Don't update workingProfile state as it should remain unchanged
      setPolicyEnforcement(policyEnforcement);
      setPolicyLimits(policyLimits);
      setApprovalScreen(approvalScreen);
      setFullSettings(updatedSettings);
      
      if ('rowVersion' in updatedSettings) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      setHasUnsavedChanges(false);
      setTabsWithChanges(new Set());
      
      // Set last saved timestamp
      setLastSaved({
        timestamp: new Date(),
        user: 'You'
      });
      
      // Re-run health check after save
      const healthResult = runHealthCheck(updatedSettings);
      setHealthCheckResult(healthResult);
      
      toast.success('Policy settings saved successfully', {
        description: 'Your policy check changes have been applied',
      });
    } catch (err) {
      console.error('Error saving policy settings:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        try {
          const serverSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
          setConflictServerSettings(serverSettings);
          setShowConflictDialog(true);
        } catch {
          toast.error('Settings conflict', {
            description: 'Settings were modified by another user. Reloading...',
          });
          await loadData();
        }
      } else {
        toast.error('Failed to save settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Save handler for Submissions & Deadlines tab (deadline settings only)
  const handleSaveGeneralSettings = async () => {
    if (!deadlineSettings) return;
    
    setIsSaving(true);
    
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }
      const updatedSettings = await settingsRepository.updateDeadlineSettings(
        deadlineSettings,
        currentSettings,
        currentRowVersion,
        mode
      );
      
      setFullSettings(updatedSettings);
      
      if ('rowVersion' in updatedSettings) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      setHasUnsavedChanges(false);
      setTabsWithChanges(new Set());
      
      // Set last saved timestamp
      setLastSaved({
        timestamp: new Date(),
        user: 'You'
      });
      
      // Re-run health check after save
      const healthResult = runHealthCheck(updatedSettings);
      setHealthCheckResult(healthResult);
      
      toast.success('Settings saved successfully', {
        description: 'Your changes have been applied',
      });
    } catch (err) {
      console.error('Error saving deadline settings:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        try {
          const serverSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
          setConflictServerSettings(serverSettings);
          setShowConflictDialog(true);
        } catch {
          toast.error('Settings conflict', {
            description: 'Settings were modified by another user. Reloading...',
          });
          await loadData();
        }
      } else {
        toast.error('Failed to save settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Save handler for General tab (data requirements only)
  const handleSaveDataRequirementsSettings = async () => {
    // Validate all settings before saving
    const validation = validateAllSettings(
      deadlineSettings,
      utilisationSettings,
      policyLimits,
      historicalEntryLimit,
      minimumEntryDuration,
      showRole,
      requireRole,
      showCostCentre,
      requireCostCentre
    );
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      toast.error('Validation failed', {
        description: `Please fix ${validation.errors.length} error${validation.errors.length > 1 ? 's' : ''} before saving`,
      });
      return;
    }
    
    // Clear validation errors
    setValidationErrors([]);
    
    setIsSaving(true);
    
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }
      
      const updatedDeadlineSettings = {
        ...deadlineSettings!,
        enableCopyPreviousWeek,
        copyDescriptions,
        copyOnlyBillable,
        enableAutoSmartFill,
        enableAiInsights,
      };
      
      const mergedSettings: OrganisationTimesheetSettings = {
        ...(currentSettings || {} as OrganisationTimesheetSettings),
        weekStartDay,
        showRole,
        requireRole,
        showCostCentre,
        requireCostCentre,
        requireDescription,
        minimumEntryDuration,
        timeRounding,
        allowNegativeHours,
        historicalEntryLimit,
        allowFutureDatedEntries,
        deadline: updatedDeadlineSettings,
      };
      
      const updatedSettings = await settingsRepository.updateOrganisationTimesheetSettings(
        mergedSettings,
        currentRowVersion,
        mode
      );
      
      setFullSettings(updatedSettings);
      setDeadlineSettings(updatedDeadlineSettings);
      
      if ('rowVersion' in updatedSettings && updatedSettings.rowVersion) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      setHasUnsavedChanges(false);
      setTabsWithChanges(new Set());
      setValidationErrors([]);
      
      // Set last saved timestamp
      setLastSaved({
        timestamp: new Date(),
        user: 'You' // In a real app, this would come from the current user context
      });
      
      // Save smart features to localStorage for role permissions access
      localStorage.setItem('globalTimesheetSettings', JSON.stringify({
        enableCopyPreviousWeek,
        copyDescriptions,
        copyOnlyBillable,
        enableAutoSmartFill,
        enableAiInsights,
      }));
      
      // Refresh global settings context to ensure all components are updated
      await refreshSettings();
      
      // Re-run health check after save
      const healthResult = runHealthCheck(updatedSettings);
      setHealthCheckResult(healthResult);
      
      toast.success('Settings saved successfully', {
        description: 'Your changes have been applied',
      });
    } catch (err) {
      console.error('Error saving data requirements:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        // Show conflict resolution dialog instead of just reloading
        try {
          const serverSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
          setConflictServerSettings(serverSettings);
          setShowConflictDialog(true);
        } catch {
          toast.error('Settings conflict', {
            description: 'Settings were modified by another user. Reloading...',
          });
          await loadData();
        }
      } else {
        toast.error('Failed to save settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="General Settings"

          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Settings2 />}
        />
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="General Settings"

          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Settings2 />}
        />
        <Alert variant="destructive">
          <AlertDescription>
            <p className="font-medium mb-2">{error}</p>
            <Button onClick={loadData} variant="outline" size="sm">
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!deadlineSettings || !utilisationSettings || !workingProfile || !policyEnforcement) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="General Settings"

          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Settings2 />}
        />
        <Alert variant="destructive">
          <AlertDescription>
            <p className="font-medium mb-2">Unable to load settings data</p>
            <p className="text-sm text-muted-foreground mb-3">
              Some required data is missing. This might be a data initialization issue.
            </p>
            <Button onClick={loadData} variant="outline" size="sm">
              Reload Settings
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
    }
  };

  const handleConfirmCancel = async () => {
    setShowCancelDialog(false);
    await loadData();
    setHasUnsavedChanges(false);
    setTabsWithChanges(new Set());
    setValidationErrors([]);
  };
  
  // Handle conflict resolution
  const handleConflictResolve = async (action: 'discard' | 'override') => {
    setShowConflictDialog(false);
    
    if (action === 'discard') {
      // Discard local changes and reload from server
      setHasUnsavedChanges(false);
      setTabsWithChanges(new Set());
      setValidationErrors([]);
      await loadData();
      toast.info('Changes discarded', {
        description: 'Your changes have been discarded and settings reloaded',
      });
    } else {
      // Override - force save with current settings
      // This would need to be implemented with a force flag in the API
      toast.warning('Override not yet implemented', {
        description: 'Please review the changes and save again',
      });
      await loadData();
    }
  };
  
  // Handle running health check on demand
  const handleRunHealthCheck = () => {
    if (fullSettings) {
      const result = runHealthCheck(fullSettings);
      setHealthCheckResult(result);
      setShowHealthCheckDialog(true);
    }
  };
  
  // Handle applying a quick fix from health check
  const handleApplyHealthCheckFix = (finding: HealthCheckFinding) => {
    if (!finding.fixAction) return;
    
    const { field, value } = finding.fixAction;
    
    // Apply the fix based on the field
    switch (field) {
      case 'deadlineDay':
        if (deadlineSettings && typeof value === 'object' && 'day' in value && 'time' in value) {
          setDeadlineSettings({
            ...deadlineSettings,
            deadlineDay: value.day,
            deadlineTime: value.time,
          });
          markTabAsChanged('submissions-deadlines');
        }
        break;
      case 'targetUtilisationRate':
        if (utilisationSettings && typeof value === 'number') {
          setUtilisationSettings({
            ...utilisationSettings,
            targetUtilisationRate: value,
          });
          markTabAsChanged('utilisation');
        }
        break;
      case 'legalMaxHoursPerWeek':
        if (policyLimits && typeof value === 'number') {
          setPolicyLimits({
            ...policyLimits,
            legalMaxHoursPerWeek: value,
          });
          markTabAsChanged('working-hours');
        }
        break;
      case 'gracePeriod':
        if (deadlineSettings && typeof value === 'number') {
          setDeadlineSettings({
            ...deadlineSettings,
            gracePeriodDays: value,
          });
          markTabAsChanged('submissions-deadlines');
        }
        break;
      case 'showDescription':
      case 'showRole':
      case 'showProject':
        if (typeof value === 'boolean') {
          if (field === 'showDescription') {
            // Note: showDescription is not currently in state, would need to add
            toast.warning('This fix requires additional implementation');
          } else if (field === 'showRole') {
            setShowRole(value);
            markTabAsChanged('general');
          } else if (field === 'showProject') {
            // Note: showProject is not currently in state, would need to add
            toast.warning('This fix requires additional implementation');
          }
        }
        break;
      case 'enableCopyPreviousWeek':
        if (typeof value === 'boolean') {
          setEnableCopyPreviousWeek(value);
          markTabAsChanged('general');
        }
        break;
      case 'historicalEntryLimit':
        if (typeof value === 'number') {
          setHistoricalEntryLimit(value);
          markTabAsChanged('general');
        }
        break;
      case 'allowNegativeHours':
        if (typeof value === 'boolean') {
          setAllowNegativeHours(value);
          markTabAsChanged('general');
        }
        break;
      case 'enableAIInsights':
        if (typeof value === 'boolean') {
          setEnableAiInsights(value);
          markTabAsChanged('general');
        }
        break;
      case 'minimumDuration':
        if (typeof value === 'number') {
          // Convert number to the enum type
          const durationMap: Record<number, '6min' | '15min' | '30min' | '1hour'> = {
            0.1: '6min',
            0.25: '15min',
            0.5: '30min',
            1: '1hour',
          };
          const duration = durationMap[value];
          if (duration) {
            setMinimumEntryDuration(duration);
            markTabAsChanged('general');
          }
        }
        break;
      case 'futureEntryLimit':
        if (typeof value === 'number') {
          const futureMap: Record<number, 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited'> = {
            0: 'no',
            1: '1week',
            2: '2weeks',
            4: '4weeks',
          };
          const future = futureMap[value];
          if (future) {
            setAllowFutureDatedEntries(future);
            markTabAsChanged('general');
          }
        }
        break;
      case 'legalMaxHoursPerDay':
        if (policyLimits && typeof value === 'number') {
          setPolicyLimits({
            ...policyLimits,
            legalMaxHoursPerDay: value,
          });
          markTabAsChanged('working-hours');
        }
        break;
      case 'sendReminders':
        if (deadlineSettings && typeof value === 'boolean') {
          setDeadlineSettings({
            ...deadlineSettings,
            sendReminders: value,
          });
          markTabAsChanged('submissions-deadlines');
        }
        break;
      default:
        toast.warning('Quick fix not available for this setting');
        return;
    }
    
    // Navigate to the affected tab if specified
    if (finding.affectedTab) {
      setActiveTab(finding.affectedTab);
    }
    
    toast.success('Fix applied', {
      description: 'Remember to save your changes',
    });
    
    // Close the health check dialog
    setShowHealthCheckDialog(false);
  };

  const handleSaveOvertimeSettings = async () => {
    setIsSaving(true);
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }

      const mergedSettings: OrganisationTimesheetSettings = {
        ...(currentSettings || DEFAULT_TIMESHEET_SETTINGS),
        overtime: overtimeSettings,
      };

      const updatedSettings = await settingsRepository.updateOrganisationTimesheetSettings(
        mergedSettings,
        currentRowVersion,
        mode
      );

      setFullSettings(updatedSettings);

      if ('rowVersion' in updatedSettings && updatedSettings.rowVersion) {
        setRowVersion(updatedSettings.rowVersion);
      }

      setHasUnsavedChanges(false);
      setTabsWithChanges(prev => {
        const next = new Set(prev);
        next.delete('overtime');
        return next;
      });

      setLastSaved({ timestamp: new Date(), user: 'You' });

      await refreshSettings();

      const healthResult = runHealthCheck(updatedSettings);
      setHealthCheckResult(healthResult);

      toast.success('Work type settings saved', {
        description: 'Your work type configuration has been updated.',
      });
    } catch (err) {
      console.error('Error saving work type settings:', err);
      toast.error('Failed to save work type settings', {
        description: err instanceof Error ? err.message : 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => {
    switch (activeTab) {
      case 'general':
        handleSaveDataRequirementsSettings();
        break;
      case 'working-hours':
        handleSaveWorkingHoursSettings();
        break;
      case 'submissions-deadlines':
        handleSaveGeneralSettings();
        break;
      case 'utilisation':
        if (utilisationSettings) {
          handleSaveUtilisation(utilisationSettings);
        }
        break;
      case 'overtime':
        handleSaveOvertimeSettings();
        break;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content area */}
      <div className="flex-1 min-h-0 overflow-auto space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="General Settings"

          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Settings2 />}
        />
        <div className="flex items-center gap-3 pt-2">
          {healthCheckResult && (
            <HealthCheckBadge 
              score={healthCheckResult.score} 
              onClick={() => setShowHealthCheckDialog(true)}
            />
          )}
          <Button 
            variant="outline" 
            onClick={handleRunHealthCheck}
            className="gap-2"
          >
            <Settings2 className="h-4 w-4" />
            Run Health Check
          </Button>
        </div>
      </div>

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
          <TabsTrigger value="working-hours" className="gap-2">
            <Clock className="h-4 w-4" />
            Working Hours & Policies
            {tabsWithChanges.has('working-hours') && (
              <span className="ml-1 h-2 w-2 rounded-full bg-amber-500" aria-label="Unsaved changes" />
            )}
          </TabsTrigger>
          <TabsTrigger value="submissions-deadlines" className="gap-2">
            <Send className="h-4 w-4" />
            Submissions & Deadlines
            {tabsWithChanges.has('submissions-deadlines') && (
              <span className="ml-1 h-2 w-2 rounded-full bg-amber-500" aria-label="Unsaved changes" />
            )}
          </TabsTrigger>
          <TabsTrigger value="general" className="gap-2">
            <FileText className="h-4 w-4" />
            Time Entry Rules
            {tabsWithChanges.has('general') && (
              <span className="ml-1 h-2 w-2 rounded-full bg-amber-500" aria-label="Unsaved changes" />
            )}
          </TabsTrigger>
          <TabsTrigger value="overtime" className="gap-2">
            <Clock className="h-4 w-4" />
            Work Type
            {tabsWithChanges.has('overtime') && (
              <span className="ml-1 h-2 w-2 rounded-full bg-amber-500" aria-label="Unsaved changes" />
            )}
          </TabsTrigger>
          <TabsTrigger value="utilisation" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Utilisation
            {tabsWithChanges.has('utilisation') && (
              <span className="ml-1 h-2 w-2 rounded-full bg-amber-500" aria-label="Unsaved changes" />
            )}
          </TabsTrigger>
        </TabsList>

        {/* Validation Error Summary */}
        {validationErrors.length > 0 && (
          <ValidationErrorSummary
            errors={validationErrors}
            onDismiss={() => setValidationErrors([])}
            onNavigateToError={(tabId) => setActiveTab(tabId)}
          />
        )}

        {/* Time Entry - Data Requirements */}
        <TabsContent value="general" className="space-y-6">
          {!canEditTimeEntryRules && (
            <Alert>
              <AlertDescription>
                You have view-only access to Time Entry Rules. Contact your administrator to request edit access.
              </AlertDescription>
            </Alert>
          )}
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && canEditTimeEntryRules && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Click "Save Settings" at the bottom to apply your changes.
              </AlertDescription>
            </Alert>
          )}

          <TimeEntryRulesAccordion
            weekStartDay={weekStartDay}
            showRole={showRole}
            requireRole={requireRole}
            showCostCentre={showCostCentre}
            requireCostCentre={requireCostCentre}
            projectSelectionScope={projectSelectionScope}
            showArchivedProjects={showArchivedProjects}
            showProjectTimeRemaining={showProjectTimeRemaining}
            minimumEntryDuration={minimumEntryDuration}
            timeRounding={timeRounding}
            allowNegativeHours={allowNegativeHours}
            historicalEntryLimit={historicalEntryLimit}
            allowFutureDatedEntries={allowFutureDatedEntries}
            enableCopyPreviousWeek={enableCopyPreviousWeek}
            copyDescriptions={copyDescriptions}
            copyOnlyBillable={copyOnlyBillable}
            enableAutoSmartFill={enableAutoSmartFill}
            enableAiInsights={enableAiInsights}
            onWeekStartDayChange={handleWeekStartDayChange}
            onShowRoleChange={handleShowRoleChange}
            onRequireRoleChange={handleRequireRoleChange}
            onShowCostCentreChange={handleShowCostCentreChange}
            onRequireCostCentreChange={handleRequireCostCentreChange}
            requireDescription={requireDescription}
            onRequireDescriptionChange={handleRequireDescriptionChange}
            onProjectSelectionScopeChange={handleProjectSelectionScopeChange}
            onShowArchivedProjectsChange={handleShowArchivedProjectsChange}
            onShowProjectTimeRemainingChange={handleShowProjectTimeRemainingChange}
            onMinimumEntryDurationChange={handleMinimumEntryDurationChange}
            onTimeRoundingChange={handleTimeRoundingChange}
            onAllowNegativeHoursChange={handleAllowNegativeHoursChange}
            onHistoricalEntryLimitChange={handleHistoricalEntryLimitChange}
            onAllowFutureDatedEntriesChange={handleAllowFutureDatedEntriesChange}
            onEnableCopyPreviousWeekChange={handleEnableCopyPreviousWeekChange}
            onCopyDescriptionsChange={handleCopyDescriptionsChange}
            onCopyOnlyBillableChange={handleCopyOnlyBillableChange}
            onEnableAutoSmartFillChange={handleEnableAutoSmartFillChange}
            onEnableAiInsightsChange={handleEnableAiInsightsChange}
          />

        </TabsContent>

        {/* Working Hours & Policies */}
        <TabsContent value="working-hours" className="space-y-6">
          {!canEditWorkingHours && (
            <Alert>
              <AlertDescription>
                You have view-only access to Working Hours &amp; Policies. Contact your administrator to request edit access.
              </AlertDescription>
            </Alert>
          )}
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && canEditWorkingHours && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Click "Save Settings" to apply your changes.
              </AlertDescription>
            </Alert>
          )}

          <WorkingHoursConfigurationCompact
            workingProfile={workingProfile}
            policyEnforcement={policyEnforcement}
            policyLimits={policyLimits}
            approvalScreen={approvalScreen}
            onWorkingProfileChange={handleWorkingProfileChange}
            onPolicyEnforcementChange={handlePolicyEnforcementChange}
            onPolicyLimitsChange={handlePolicyLimitsChange}
            onApprovalScreenChange={handleApprovalScreenChange}
          />
        </TabsContent>

        {/* Submissions & Deadlines */}
        <TabsContent value="submissions-deadlines" className="space-y-6">
          {!canEditDeadlines && (
            <Alert>
              <AlertDescription>
                You have view-only access to Submissions &amp; Deadlines. Contact your administrator to request edit access.
              </AlertDescription>
            </Alert>
          )}
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && canEditDeadlines && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Click "Save Settings" at the bottom to apply your changes.
              </AlertDescription>
            </Alert>
          )}

          <TimesheetDeadlineSettings
            settings={deadlineSettings}
            onChange={handleDeadlineChange}
            onNavigateToNotifications={() => onNavigate?.('/settings/notifications')}
          />
        </TabsContent>

        {/* Work Type */}
        <TabsContent value="overtime" className="space-y-6">
          {!canEditWorkType && (
            <Alert>
              <AlertDescription>
                You have view-only access to Work Type. Contact your administrator to request edit access.
              </AlertDescription>
            </Alert>
          )}
          {tabsWithChanges.has('overtime') && canEditWorkType && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Click &quot;Save Settings&quot; at the bottom to apply your changes.
              </AlertDescription>
            </Alert>
          )}
          <OvertimeSettingsTab
            settings={overtimeSettings}
            onChange={handleOvertimeSettingsChange}
            companyId={companyId}
            types={overtimeTypes}
            onTypesChange={refetchOvertimeTypes}
          />
        </TabsContent>

        {/* Utilisation */}
        <TabsContent value="utilisation" className="space-y-6">
          {!canEditUtilisation && (
            <Alert>
              <AlertDescription>
                You have view-only access to Utilisation. Contact your administrator to request edit access.
              </AlertDescription>
            </Alert>
          )}
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && canEditUtilisation && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Click "Save Settings" to apply your changes.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Utilisation & Performance</CardTitle>
              <CardDescription>
                Configure utilisation tracking and performance metrics
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <UtilisationSettings
                settings={utilisationSettings}
                onChange={handleUtilisationChange}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>

      {/* Footer - always visible at bottom */}
      <div className="shrink-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 -mx-2 sm:-mx-3 lg:-mx-3 xl:-mx-4 px-2 sm:px-3 lg:px-3 xl:px-4">
        <div className="flex h-16 items-center justify-between gap-4 max-w-[2000px] mx-auto">
          {/* Last saved timestamp */}
          <div className="text-sm text-muted-foreground">
            {lastSaved && (
              <span>
                Last saved {formatDistanceToNow(lastSaved.timestamp, { addSuffix: true })} by {lastSaved.user}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              size="lg"
              className="min-w-[120px]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges || (() => {
                switch (activeTab) {
                  case 'working-hours':       return !canEditWorkingHours;
                  case 'submissions-deadlines': return !canEditDeadlines;
                  case 'general':             return !canEditTimeEntryRules;
                  case 'overtime':            return !canEditWorkType;
                  case 'utilisation':         return !canEditUtilisation;
                  default:                    return false;
                }
              })()}
              title={(() => {
                const tabBlocked = (() => {
                  switch (activeTab) {
                    case 'working-hours':       return !canEditWorkingHours;
                    case 'submissions-deadlines': return !canEditDeadlines;
                    case 'general':             return !canEditTimeEntryRules;
                    case 'overtime':            return !canEditWorkType;
                    case 'utilisation':         return !canEditUtilisation;
                    default:                    return false;
                  }
                })();
                return tabBlocked ? 'You do not have permission to edit this settings tab' : undefined;
              })()}
              size="lg"
              className="min-w-[120px]"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Conflict Resolution Dialog */}
      {showConflictDialog && conflictServerSettings && fullSettings && (
        <ConflictResolutionDialog
          open={showConflictDialog}
          currentSettings={fullSettings}
          serverSettings={conflictServerSettings}
          onResolve={handleConflictResolve}
          onCancel={() => setShowConflictDialog(false)}
        />
      )}
      
      {/* Health Check Dialog */}
      {showHealthCheckDialog && healthCheckResult && (
        <HealthCheckDialog
          result={healthCheckResult}
          onClose={() => setShowHealthCheckDialog(false)}
          onApplyFix={handleApplyHealthCheckFix}
        />
      )}
    </div>
  );
};
